import { useNavigate } from "@tanstack/react-location"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { reportChatError } from "../../reportChatError"
import { useChatRuntime, useChatState } from "../../hooks/useChat"
import { usePeerProfiles } from "../../hooks/usePeerProfiles"
import {
  canNotify,
  setNotificationsUnsupported,
  notificationContent,
} from "../../runtime/notifications"
import { conversationFor } from "../../runtime/selectors"
import type { PendingNotification } from "../../runtime/store"
import { isRoomJid } from "../../xmpp/jid"
import { conversationPath } from "../MessagesView/conversationPath"

/** How long a notification waits for the sender's name before firing without it. */
const NAME_WAIT_MS = 2000

/**
 * Fires the OS notifications the store asked for (FAME-858). Mounted inside the
 * router, so the click can route. `<ChatProvider>` sits above the router and
 * keeps queueing even while this is unmounted, hence the queue's cap.
 */
export function MessageNotifier() {
  const pending = useChatState((state) => state.pendingNotifications)

  return (
    <>
      {pending.map((entry) => (
        <PendingNotificationFirer
          key={`${entry.peer}/${entry.id}`}
          pending={entry}
        />
      ))}
    </>
  )
}

/**
 * One queued notification. Waits up to `NAME_WAIT_MS` for the name, fires, and
 * removes itself from the queue — fired, skipped or failed alike.
 */
function PendingNotificationFirer({
  pending,
}: {
  pending: PendingNotification
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { clearNotification } = useChatRuntime().actions

  const activePeer = useChatState((state) => state.activePeer)
  const conversation = useChatState((state) =>
    conversationFor(state, pending.peer)
  )
  const [waited, setWaited] = useState(false)
  const handled = useRef(false)

  const isGroup = isRoomJid(pending.peer)
  // A group is titled by its stored name, so its room JID stays out of the peer
  // batch; the sender's handle is resolved for a group body's prefix.
  // No toast: every arriving message mounts one of these, on any route.
  const { isResolving, failed, resolvePeer, resolveHandle } = usePeerProfiles(
    isGroup ? [] : [pending.peer],
    pending.author ? [pending.author] : [],
    undefined,
    { toastOnFailure: false }
  )

  useEffect(() => {
    const timer = setTimeout(() => setWaited(true), NAME_WAIT_MS)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (handled.current || (isResolving && !waited)) return
    handled.current = true

    // Permission can be revoked, the reader can return, or the conversation can
    // be muted while the name was coming — any of these leaves nothing to fire.
    const viewingNow = activePeer === pending.peer && document.hasFocus()
    if (!viewingNow && !conversation?.muted && canNotify()) {
      const named = !isResolving && !failed
      const { title, body, tag } = notificationContent({
        peer: pending.peer,
        messageId: pending.id,
        isGroup,
        conversationName: isGroup
          ? (conversation?.name ??
            String(t("chat.NOTIFICATION_GROUP_FALLBACK")))
          : named
            ? resolvePeer(pending.peer).displayName
            : String(t("chat.NOTIFICATION_TITLE_FALLBACK")),
        body: pending.body || String(t("chat.NOTIFICATION_ATTACHMENT")),
        formatGroupBody: (line) =>
          String(
            t("chat.PREVIEW_SENDER", {
              name: named
                ? resolveHandle(pending.author).displayName
                : t("chat.PROFILE_SOMEONE"),
              body: line,
            })
          ),
      })

      // Android Chrome has no constructor and throws; that must not take the app down.
      try {
        const notification = new Notification(title, { body, tag })
        notification.onclick = () => {
          window.focus()
          navigate({ to: conversationPath(pending.peer) })
          notification.close()
        }
      } catch (error) {
        // Reported once: nothing is enqueued or fired after this.
        setNotificationsUnsupported()
        reportChatError("failed to show a notification", error)
      }
    }

    clearNotification(pending.peer, pending.id)
  }, [
    isResolving,
    failed,
    waited,
    activePeer,
    conversation,
    isGroup,
    pending,
    resolvePeer,
    resolveHandle,
    navigate,
    clearNotification,
    t,
  ])

  return null
}
